import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { Button, Form, Input, message, Modal, Popconfirm, Select, Table, Tooltip } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";

const { RangePicker } = DatePicker;

const ApproveDiscount = ({ onApproval }) => {
  const getCurrentAcademicYear = () => {
    const today = new Date();
    const curYear = today.getFullYear();
    const curMonth = today.getMonth() + 1;
    const startYear = curMonth >= 6 ? curYear : curYear - 1;
    const endYearShort = String(startYear + 1).slice(-2);
    return `${startYear}-${endYearShort}`;
  };

  const academicYears = [getCurrentAcademicYear()];
  const user = useSelector(selectUser);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { Option } = Select;

  const [selectedYear, setSelectedYear] = useState(getCurrentAcademicYear());
  const [classId, setClassId] = useState();
  const [studentSearch, setStudentSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkApproveLoading, setBulkApproveLoading] = useState(false);
  const [bulkRejectLoading, setBulkRejectLoading] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);

  const canApprove = user?.admin?.can_approve_transaction;

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const getDiscountBreakdown = (record) => {
    const type = record?.fee_template?.type;
    const isTuitionTemplate = type?.toLowerCase() === "tution";
    const installmentList = record?.installment_list || [];
    const otherFeeInst = installmentList.find(
      (inst) => inst.name?.toLowerCase() === "other fee",
    );
    const otherTotal = otherFeeInst
      ? Number(
          otherFeeInst.payable_components?.[0]?.total_amount ||
            otherFeeInst.payable_components?.total_amount ||
            0,
        )
      : 0;
    const otherPaid = otherFeeInst?.paid || 0;
    const otherRemaining = otherFeeInst?.remaining || 0;

    if (record?.tempMiscFeeType) {
      return { total: otherTotal, paid: otherPaid, remaining: otherRemaining };
    }

    let total = record?.total || 0;
    let paid = record?.paid || 0;
    let remaining = record?.remaining || 0;

    if (isTuitionTemplate) {
      // Sum payable_components.total_amount for tuition installments — not reduced by discount
      const tuitionInsts = installmentList.filter(
        (inst) => inst.name?.toLowerCase() !== "other fee"
      );
      total = tuitionInsts.length > 0
        ? tuitionInsts.reduce((sum, inst) => {
            const pc = Array.isArray(inst.payable_components)
              ? inst.payable_components[0]
              : (inst.payable_components || {});
            return sum + Number(pc?.total_amount || 0);
          }, 0)
        : (record?.total || 0) + (record?.discount || 0);
      paid = (record?.paid || 0) - otherPaid;
      remaining = (record?.remaining || 0) - otherRemaining;
    }
    return { total, paid, remaining };
  };

  const getDiscountAmount = (record) =>
    record?.discountStatus === "approved"
      ? record?.discount
      : record?.tempDiscountAmount;

  const getSelectedTermsLabel = (record) => {
    if (record?.tempMiscFeeType) {
      const raw = record.tempMiscFeeType;
      const label =
        raw.startsWith("__") && raw.endsWith("__")
          ? "Miscellaneous"
          : raw.charAt(0).toUpperCase() + raw.slice(1);
      return label;
    }
    let indices =
      record?.discountStatus === "approved"
        ? record?.discountTermIndices || []
        : record?.tempDiscountTermIndices || [];

    const templateInsts = record?.fee_template?.installment_list?.length
      ? record.fee_template.installment_list
      : record?.installment_list || [];

    const otherFeeIdx = templateInsts.findIndex(
      (inst) => inst.name?.toLowerCase() === "other fee",
    );
    if (otherFeeIdx !== -1)
      indices = indices.filter((idx) => idx !== otherFeeIdx);
    if (indices.length === 0) return "—";
    const eligibleInsts = templateInsts.filter(
      (inst) => inst.name?.toLowerCase() !== "other fee",
    );
    if (indices.length === eligibleInsts.length) return "All Terms";
    return indices
      .map((idx) => templateInsts[idx]?.name || `Term ${idx + 1}`)
      .join(", ");
  };

  const getFeeTypeLabel = (record) => {
    if (record?.tempMiscFeeType) {
      const raw = record.tempMiscFeeType;
      return raw.startsWith("__") && raw.endsWith("__")
        ? "Miscellaneous"
        : raw.charAt(0).toUpperCase() + raw.slice(1);
    }
    const type = record?.fee_template?.type;
    if (type?.toLowerCase() === "tution") return "Tuition";
    if (type?.toLowerCase() === "other fee") return "Misc";
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : "—";
  };

  const openView = (record) => {
    const breakdown = getDiscountBreakdown(record);
    const student = record?.student;
    setViewRecord(record);
    form.setFieldsValue({
      fullName: student
        ? `${student.first_name || ""} ${student.last_name || ""}`.trim()
        : "—",
      class: record?.class?.name,
      feeType: getFeeTypeLabel(record),
      totalFee: breakdown.total,
      discountAmount: getDiscountAmount(record),
      reason:
        record?.discountStatus === "approved"
          ? record?.reason
          : record?.tempDiscountReason,
      terms: getSelectedTermsLabel(record),
    });
  };


  const approveOne = (record) =>
    axios.patch(
      `${serverUrl}/admin/student-fee/${record?._id}/approve-discount`,
      {},
      {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      },
    );

  const rejectOne = (record) =>
    axios.patch(
      `${serverUrl}/admin/student-fee/${record?._id}/reject-discount`,
      {},
      {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      },
    );

  const refresh = () => {
    queryClient.invalidateQueries(["pending-discounts", selectedYear, classId]);
    onApproval?.();
  };

  const closeView = () => {
    setViewRecord(null);
    form.resetFields();
  };

  const handleApprove = async (record, fromModal = false) => {
    if (fromModal) closeView();
    try {
      await approveOne(record);
      message.success("Discount approved");
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.error?.errors || "Approval failed");
    }
  };

  const handleReject = async (record, fromModal = false) => {
    if (fromModal) closeView();
    try {
      await rejectOne(record);
      message.success("Discount request rejected");
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.error?.errors || "Rejection failed");
    }
  };

  const handleBulkApprove = async () => {
    setBulkApproveLoading(true);
    let successCount = 0;
    let failCount = 0;
    for (const key of selectedRowKeys) {
      const record = tableData.find((r) => r._id === key);
      if (record?.discountStatus === "pending") {
        try {
          await approveOne(record);
          successCount++;
        } catch (_) {
          failCount++;
        }
      }
    }
    setBulkApproveLoading(false);
    setSelectedRowKeys([]);
    refresh();
    if (successCount > 0 && failCount === 0) {
      message.success(`${successCount} discount(s) approved`);
    } else if (successCount > 0 && failCount > 0) {
      message.warning(`${successCount} approved, ${failCount} failed`);
    } else if (failCount > 0) {
      message.error(`All ${failCount} approval(s) failed`);
    }
  };

  const handleBulkReject = async () => {
    setBulkRejectLoading(true);
    let successCount = 0;
    let failCount = 0;
    for (const key of selectedRowKeys) {
      const record = tableData.find((r) => r._id === key);
      if (record?.discountStatus === "pending") {
        try {
          await rejectOne(record);
          successCount++;
        } catch (_) {
          failCount++;
        }
      }
    }
    setBulkRejectLoading(false);
    setSelectedRowKeys([]);
    refresh();
    if (successCount > 0 && failCount === 0) {
      message.success(`${successCount} discount(s) rejected`);
    } else if (successCount > 0 && failCount > 0) {
      message.warning(`${successCount} rejected, ${failCount} failed`);
    } else if (failCount > 0) {
      message.error(`All ${failCount} rejection(s) failed`);
    }
  };

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchClasses = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classes = useQuery("classes", fetchClasses);

  const fetchPendingDiscounts = async (selectedYear, classId) => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/admin/student-fee/pending-discounts",
      params: { year: selectedYear, ...(classId && { classId }) },
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data?.result || [];
  };
  const pendingDiscountsData = useQuery(
    ["pending-discounts", selectedYear, classId],
    () => fetchPendingDiscounts(selectedYear, classId),
  );

  // ── Client-side filtering ────────────────────────────────────────────────────

  const applyDateFilter = (data) => {
    if (!dateRange[0] || !dateRange[1]) return data;
    return data.filter((r) => {
      const d = moment(r?.updatedAt || r?.createdAt);
      return (
        d.isSameOrAfter(dateRange[0], "day") &&
        d.isSameOrBefore(dateRange[1], "day")
      );
    });
  };

  const tableData = (() => {
    let data = pendingDiscountsData?.data || [];
    if (studentSearch.trim()) {
      const q = studentSearch.trim().toLowerCase();
      data = data.filter((r) => {
        const s = r?.student;
        const name = s
          ? `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase()
          : "";
        return name.includes(q);
      });
    }
    data = applyDateFilter(data);
    if (statusFilter !== "all") {
      data = data.filter((r) => r?.discountStatus === statusFilter);
    }

    data = [...data].sort((a, b) => {
      const statusOrder = (s) => (s === "pending" ? 0 : 1);
      const statusDiff = statusOrder(a?.discountStatus) - statusOrder(b?.discountStatus);
      if (statusDiff !== 0) return statusDiff;
      if (a?.discountStatus === "pending") {
        return moment(a?.createdAt).valueOf() - moment(b?.createdAt).valueOf();
      }
      return moment(b?.createdAt).valueOf() - moment(a?.createdAt).valueOf();
    });

    return data;
  })();

  const pendingSelected = selectedRowKeys.filter((key) => {
    const r = tableData.find((t) => t._id === key);
    return r?.discountStatus === "pending";
  });

  // ── Summary cards (date-filtered, all statuses) ──────────────────────────────

  const dateFilteredAll = applyDateFilter(pendingDiscountsData?.data || []);
  const pendingAll = dateFilteredAll.filter(
    (r) => r?.discountStatus === "pending",
  );
  const approvedAll = dateFilteredAll.filter(
    (r) => r?.discountStatus === "approved",
  );

  const sumDiscount = (arr) =>
    arr.reduce((s, r) => s + (Number(getDiscountAmount(r)) || 0), 0);
  const fmt = (n) => `₹ ${Number(n).toLocaleString("en-IN")}`;

  const dateRangeLabel =
    dateRange[0] && dateRange[1]
      ? `${moment(dateRange[0]).format("DD MMM")} – ${moment(dateRange[1]).format("DD MMM YYYY")}`
      : "All dates";


  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#8c8c8c",
    marginBottom: 6,
    display: "block",
  };

  const statusPill = (status) => {
    const cfg = {
      pending: {
        bg: "#fff7e6",
        color: "#d46b08",
        border: "#ffd591",
        label: "Pending",
      },
      approved: {
        bg: "#f6ffed",
        color: "#389e0d",
        border: "#b7eb8f",
        label: "Approved",
      },
      rejected: {
        bg: "#fff1f0",
        color: "#cf1322",
        border: "#ffa39e",
        label: "Rejected",
      },
    };
    const c = cfg[status] || {
      bg: "#fafafa",
      color: "#595959",
      border: "#d9d9d9",
      label: status || "--",
    };
    return (
      <span
        style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.03em",
          background: c.bg,
          color: c.color,
          border: `1px solid ${c.border}`,
        }}
      >
        {c.label}
      </span>
    );
  };

  // ── Columns ──────────────────────────────────────────────────────────────────

  const columns = [
    {
      title: "Student",
      align: "left",
      render: (r) => {
        const s = r?.student;
        const name = s
          ? `${s.first_name || ""} ${s.last_name || ""}`.trim()
          : "—";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #722ed122, #722ed144)",
                color: "#722ed1",
                fontSize: 12,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {initials}
            </div> */}
            <span
              style={{
                fontWeight: 600,
                color: "#262626",
                textTransform: "capitalize",
              }}
            >
              {name}
            </span>
          </div>
        );
      },
    },
    {
      title: "Class",
      align: "center",
      width: 75,
      render: (r) => (
        <span style={{ fontWeight: 500, color: "#434343" }}>
          {r?.class?.name || "—"}
        </span>
      ),
    },
    {
      title: "Fee Type",
      align: "center",
      width: 130,
      render: (r) => (
        <span
          style={{
            display: "inline-block",
            padding: "2px 10px",
            borderRadius: 12,
            background: "#f9f0ff",
            color: "#722ed1",
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          {getFeeTypeLabel(r)}
        </span>
      ),
    },
    {
      title: "Original Fee",
      align: "right",
      width: 115,
      render: (r) => {
        const breakdown = getDiscountBreakdown(r);
        return (
          <span style={{ fontWeight: 600, color: "#434343" }}>
            {fmt(breakdown.total || 0)}
          </span>
        );
      },
    },
    {
      title: "Discount",
      align: "right",
      width: 110,
      render: (r) => (
        <span style={{ fontWeight: 700, color: "#d46b08", fontSize: 14 }}>
          {fmt(getDiscountAmount(r) || 0)}
        </span>
      ),
    },
    {
      title: "Terms",
      align: "center",
      width: 120,
      render: (r) => (
        <span style={{ fontSize: 12, color: "#595959" }}>
          {getSelectedTermsLabel(r)}
        </span>
      ),
    },
    {
      title: "Reason",
      align: "left",
      render: (r) => {
        const reason =
          r?.discountStatus === "approved" ? r?.reason : r?.tempDiscountReason;
        return (
          <span style={{ fontSize: 12, fontStyle: "italic", color: "#8c8c8c" }}>
            {reason || "—"}
          </span>
        );
      },
    },
    {
      title: "Status",
      align: "center",
      width: 100,
      render: (r) => statusPill(r?.discountStatus),
    },
    {
      title: "Actions",
      align: "center",
      width: 120,
      render: (r) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {r?.discountStatus === "pending" && canApprove && (
            <>
              <Popconfirm
                title="Approve this discount?"
                onConfirm={() => handleApprove(r)}
                okText="Approve"
                cancelText="Cancel"
              >
                <Tooltip title="Approve">
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      background: "#f6ffed",
                      border: "1px solid #b7eb8f",
                    }}
                  >
                    <CheckCircleOutlined
                      style={{ fontSize: 16, color: "#52c41a" }}
                    />
                  </div>
                </Tooltip>
              </Popconfirm>
              <Popconfirm
                title="Reject this discount request?"
                onConfirm={() => handleReject(r)}
                okText="Reject"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Reject">
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      background: "#fff1f0",
                      border: "1px solid #ffa39e",
                    }}
                  >
                    <CloseCircleOutlined
                      style={{ fontSize: 16, color: "#ff4d4f" }}
                    />
                  </div>
                </Tooltip>
              </Popconfirm>
            </>
          )}
          <Tooltip title="View details">
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                background: "#e6f7ff",
                border: "1px solid #91d5ff",
              }}
              onClick={() => openView(r)}
            >
              <EyeOutlined style={{ fontSize: 16, color: "#1890ff" }} />
            </div>
          </Tooltip>
        </div>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: "24px 0" }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        {/* ── Page header ── */}
        <div
          style={{
            padding: "20px 28px 16px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#1a1a1a",
              }}
            >
              Discount Approvals
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "#8c8c8c" }}>
              {tableData.length} request{tableData.length !== 1 ? "s" : ""}{" "}
              shown
            </p>
          </div>
          {statusPill(statusFilter === "all" ? null : statusFilter)}
        </div>

        {/* ── Filters ── */}
        <div
          style={{
            padding: "16px 28px",
            background: "#fafafa",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "flex-end",
          }}
        >
          <div
            style={{ display: "flex", flexDirection: "column", minWidth: 100 }}
          >
            <span style={labelStyle}>Class</span>
            <Select
              showSearch
              allowClear
              placeholder="All classes"
              style={{ width: "100%" }}
              onChange={(v) => setClassId(v)}
            >
              {classes?.data?.map((d) => (
                <Option key={d._id} value={d._id}>
                  {d.name}
                </Option>
              ))}
            </Select>
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", minWidth: 100 }}
          >
            <span style={labelStyle}>Academic Year</span>
            <Select
              value={selectedYear}
              onChange={(v) => setSelectedYear(v)}
              style={{ width: "100%" }}
            >
              {academicYears.map((yr) => (
                <Option key={yr} value={yr}>
                  {yr}
                </Option>
              ))}
            </Select>
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", minWidth: 200 }}
          >
            <span style={labelStyle}>Student Name</span>
            <Input
              placeholder="Search by name..."
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
            />
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", minWidth: 100 }}
          >
            <span style={labelStyle}>Request Date</span>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [null, null])}
              format="DD MMM YYYY"
              allowClear
            />
          </div>  
          <div
            style={{ display: "flex", flexDirection: "column", minWidth: 140 }}
          >
            <span style={labelStyle}>Status</span>
            <Select
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              style={{ width: "100%" }}
            >
              <Option value="all">All Statuses</Option>
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </div>
        </div>

        {/* ── Summary cards ── */}
        <div style={{ padding: "20px 28px 4px" }}>
          <div
            style={{
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#8c8c8c",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Discount Summary
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 20,
                background: "#f0f0f0",
                color: "#595959",
                border: "1px solid #e0e0e0",
              }}
            >
              {dateRangeLabel}
            </span>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 14,
            }}
          >
            {/* Pending discounts */}
            <div
              style={{
                background: "linear-gradient(135deg, #fff7e6, #ffe7ba)",
                border: "1px solid #ffd591",
                borderRadius: 10,
                padding: "16px 18px",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  color: "#d46b08",
                  marginBottom: 6,
                }}
              >
                Awaiting Approval
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#ad4e00",
                  lineHeight: 1.2,
                }}
              >
                {fmt(sumDiscount(pendingAll))}
              </div>
              <div style={{ fontSize: 12, color: "#fa8c16", marginTop: 4 }}>
                {pendingAll.length} request{pendingAll.length !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Approved discounts */}
            <div
              style={{
                background: "linear-gradient(135deg, #f6ffed, #d9f7be)",
                border: "1px solid #b7eb8f",
                borderRadius: 10,
                padding: "16px 18px",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  color: "#389e0d",
                  marginBottom: 6,
                }}
              >
                Total Approved
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#237804",
                  lineHeight: 1.2,
                }}
              >
                {fmt(sumDiscount(approvedAll))}
              </div>
              <div style={{ fontSize: 12, color: "#52c41a", marginTop: 4 }}>
                {approvedAll.length} approved
              </div>
            </div>

            {/* Pending tuition discounts */}
            {/* <div style={{
              background: "linear-gradient(135deg, #e6f7ff, #bae7ff)",
              border: "1px solid #91d5ff", borderRadius: 10, padding: "16px 18px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#096dd9", marginBottom: 6 }}>
                Tuition Pending
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0050b3", lineHeight: 1.2 }}>
                {fmt(sumDiscount(pendingTuition))}
              </div>
              <div style={{ fontSize: 12, color: "#1890ff", marginTop: 4 }}>
                {pendingTuition.length} request{pendingTuition.length !== 1 ? "s" : ""}
              </div>
            </div> */}

            {/* Pending misc discounts */}
            {/* <div style={{
              background: "linear-gradient(135deg, #f9f0ff, #efdbff)",
              border: "1px solid #d3adf7", borderRadius: 10, padding: "16px 18px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#531dab", marginBottom: 6 }}>
                Misc Pending
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#391085", lineHeight: 1.2 }}>
                {fmt(sumDiscount(pendingMisc))}
              </div>
              <div style={{ fontSize: 12, color: "#722ed1", marginTop: 4 }}>
                {pendingMisc.length} request{pendingMisc.length !== 1 ? "s" : ""}
              </div>
            </div> */}
          </div>
        </div>

        {/* ── Bulk action bar ── */}
        {selectedRowKeys.length > 0 && (
          <div
            style={{
              margin: "12px 28px",
              padding: "10px 16px",
              borderRadius: 8,
              background: "linear-gradient(90deg, #f9f0ff, #f0f5ff)",
              border: "1px solid #d3adf7",
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "nowrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginRight: 4,
              }}
            >
              <span
                style={{
                  background: "#722ed1",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "1px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 20,
                }}
              >
                {selectedRowKeys.length}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#391085", display: "inline-flex", alignItems: "center" }}>
                selected &nbsp;·&nbsp; {pendingSelected.length} pending
              </span>
            </div>
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              loading={bulkApproveLoading}
              disabled={pendingSelected.length === 0 || !canApprove}
              onClick={handleBulkApprove}
              style={{ borderRadius: 6, display: "inline-flex", alignItems: "center" }}
            >
              Approve{" "}
              {pendingSelected.length > 0 ? `(${pendingSelected.length})` : ""}
            </Button>
            <Popconfirm
              title={`Reject ${pendingSelected.length} discount request(s)?`}
              onConfirm={handleBulkReject}
              okText="Reject"
              okButtonProps={{ danger: true }}
              disabled={pendingSelected.length === 0 || !canApprove}
            >
              <Button
                size="small"
                danger
                icon={<CloseCircleOutlined />}
                loading={bulkRejectLoading}
                disabled={pendingSelected.length === 0 || !canApprove}
                style={{ borderRadius: 6, display: "inline-flex", alignItems: "center" }}
              >
                Reject{" "}
                {pendingSelected.length > 0
                  ? `(${pendingSelected.length})`
                  : ""}
              </Button>
            </Popconfirm>
            <Button
              size="small"
              onClick={() => setSelectedRowKeys([])}
              style={{ borderRadius: 6, marginLeft: "auto", display: "inline-flex", alignItems: "center" }}
            >
              Clear selection
            </Button>
          </div>
        )}

        {/* ── Table ── */}
        <div style={{ padding: "0 28px 24px" }}>
          <Table
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
              getCheckboxProps: (r) => ({
                disabled: r?.discountStatus !== "pending",
              }),
            }}
            rowKey="_id"
            columns={columns}
            dataSource={tableData}
            pagination={{
              pageSize: 15,
              showSizeChanger: false,
              showTotal: (total) => `${total} requests`,
              style: { marginTop: 16 },
            }}
            loading={pendingDiscountsData?.isLoading}
            size="middle"
          />
        </div>

        {/* ── View / Approve modal ── */}
        <Modal
          width={440}
          visible={!!viewRecord}
          onCancel={closeView}
          footer={null}
          bodyStyle={{ padding: 0 }}
          style={{ top: 60 }}
          destroyOnClose
          focusTriggerAfterClose={false}
        >
          {/* Modal header */}
          <div
            style={{
              padding: "20px 24px 16px",
              borderBottom: "1px solid #f0f0f0",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#8c8c8c",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Discount Request
              </p>
              <h3
                style={{
                  margin: "4px 0",
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#1a1a1a",
                }}
              >
                {viewRecord?.student
                  ? `${viewRecord.student.first_name || ""} ${viewRecord.student.last_name || ""}`.trim()
                  : "—"}
              </h3>
              {statusPill(viewRecord?.discountStatus)}
            </div>
            <div style={{ textAlign: "right", fontSize: 13, color: "#8c8c8c" }}>
              <div style={{ fontWeight: 500 }}>{viewRecord?.class?.name}</div>
              <div style={{ marginTop: 2 }}>
                {getFeeTypeLabel(viewRecord || {})}
              </div>
            </div>
          </div>

          <div style={{ padding: "20px 24px" }}>
            <Form layout="vertical" form={form}>
              {/* Student + class */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <Form.Item
                  label="Student"
                  name="fullName"
                  style={{ flex: 2, marginBottom: 0 }}
                >
                  <Input readOnly style={{ background: "#fafafa" }} />
                </Form.Item>
                <Form.Item
                  label="Class"
                  name="class"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <Input readOnly style={{ background: "#fafafa" }} />
                </Form.Item>
              </div>

              <Form.Item
                label="Fee Type"
                name="feeType"
                style={{ marginBottom: 16 }}
              >
                <Input readOnly style={{ background: "#fafafa" }} />
              </Form.Item>

              {/* Fee + Discount highlight */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <div
                  style={{
                    flex: 1,
                    background: "#fafafa",
                    border: "1px solid #f0f0f0",
                    borderRadius: 8,
                    padding: "12px 14px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: "#8c8c8c",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 4,
                    }}
                  >
                    Original Fee
                  </div>
                  <div
                    style={{ fontSize: 18, fontWeight: 700, color: "#434343" }}
                  >
                    {fmt(form.getFieldValue("totalFee") || 0)}
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    background: "linear-gradient(135deg, #fff7e6, #ffe7ba)",
                    border: "1px solid #ffd591",
                    borderRadius: 8,
                    padding: "12px 14px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: "#d46b08",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 4,
                    }}
                  >
                    Discount Requested
                  </div>
                  <div
                    style={{ fontSize: 22, fontWeight: 800, color: "#ad4e00" }}
                  >
                    {fmt(form.getFieldValue("discountAmount") || 0)}
                  </div>
                </div>
              </div>

              <Form.Item
                label="Terms to Apply"
                name="terms"
                style={{ marginBottom: 12 }}
              >
                <Input readOnly style={{ background: "#fafafa" }} />
              </Form.Item>

              <Form.Item
                label="Reason"
                name="reason"
                style={{ marginBottom: 20 }}
              >
                <Input.TextArea
                  readOnly
                  rows={3}
                  style={{ background: "#fafafa", resize: "none" }}
                />
              </Form.Item>

              {/* Action buttons */}
              <div
                style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}
              >
                {viewRecord?.discountStatus === "pending" && canApprove && (
                  <>
                    <Button
                      type="primary"
                      style={{ borderRadius: 6, flex: 1 }}
                      onClick={() => handleApprove(viewRecord, true)}
                    >
                      Approve
                    </Button>
                    <Popconfirm
                      title="Reject this discount request?"
                      onConfirm={() => handleReject(viewRecord, true)}
                      okText="Reject"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        danger
                        style={{ borderRadius: 6, flex: 1 }}
                      >
                        Reject
                      </Button>
                    </Popconfirm>
                  </>
                )}
                <Button onClick={closeView} style={{ borderRadius: 6 }}>
                  Close
                </Button>
              </div>
            </Form>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ApproveDiscount;
